import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import log from 'loglevel';
import * as Sentry from '@sentry/nextjs';
import router from 'next/router';
import { Routes } from 'services/routes';
import { ApiResult } from './result.type';

interface RequestOptions {
  redirectOn401: boolean;
}

const defaultOptions = Object.freeze({
  redirectOn401: true,
});

export abstract class BaseApi {
  protected abstract api: AxiosInstance;

  async get<T>(
    url: string,
    config?: AxiosRequestConfig,
    options: RequestOptions = defaultOptions,
  ): ApiResult<T> {
    return await BaseApi.request(
      async () => await this.api.get<T>(url, config),
      options,
    );
  }

  async post<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
    options: RequestOptions = defaultOptions,
  ): ApiResult<T> {
    return await BaseApi.request(
      async () => await this.api.post<T>(url, data, config),
      options,
    );
  }

  async patch<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
    options: RequestOptions = defaultOptions,
  ): ApiResult<T> {
    return await BaseApi.request(
      async () => await this.api.patch<T>(url, data, config),
      options,
    );
  }

  async put<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
    options: RequestOptions = defaultOptions,
  ): ApiResult<T> {
    return await BaseApi.request(
      async () => await this.api.put<T>(url, data, config),
      options,
    );
  }

  async delete<T>(
    url: string,
    config?: AxiosRequestConfig,
    options: RequestOptions = defaultOptions,
  ): ApiResult<T> {
    return await BaseApi.request(
      async () => await this.api.delete<T>(url, config),
      options,
    );
  }

  private static async request<T>(
    makeRequest: () => Promise<AxiosResponse<T>>,
    { redirectOn401 = true }: Partial<RequestOptions> = {},
  ): ApiResult<T> {
    try {
      const response = await makeRequest();
      return { success: true, value: response.data };
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        log.error(error);
        Sentry.captureException(error);
        throw error;
      }

      if (!error.response) {
        error.message = 'Internet connection lost.';
      } else if (error.response?.status === 400) {
        error.message = (error.response.data as { message: string }).message;
      } else if (error.response?.status === 401 && redirectOn401) {
        await router.push(Routes.logIn(router.asPath));
        error.message = 'Session timed out. Please log back in';
      } else if (error.response?.status > 401) {
        error.message = 'Server Error. Please try again later';
        log.error(error);
        Sentry.captureException(error);
      }

      return { success: false, error };
    }
  }
}
